"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, use, useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import type { BeaconData } from "./types";
import { StoreTrackingScripts } from "./StoreTrackingScripts";
import { STAGE } from "@local/utils/src/services";
import { ensurePosthogClient, isPostHogEnabled, registerPostHogProperties } from "../scripts/posthog";
import { readUpsellChannel } from "../upsellChannelStorage";

/** Params that are data-only — available via window globals / cookies,
 *  so we strip them from the URL instead of injecting and re-stripping. */
const DATA_ONLY_PARAMS = ["vb", "country", "currency"
// "nuclick" is intentionally excluded — NuClickBeacon owns its removal
// after GTM has had a chance to read it on the initial page_view
];

/** Kick off the deferred PostHog init. Idempotent — the helper is backed by
 *  a module-level singleton promise. No-op in apps that didn't call
 *  enablePostHog() in their instrumentation-client.ts. */
const bootPosthog = () => {
  if (!isPostHogEnabled()) return;
  ensurePosthogClient().catch(() => {
    // ensurePosthogClient already logs and resets internally
  });
};
interface Props {
  dataPromise: Promise<BeaconData | null>;
  buildCheckoutUrl?: boolean;
  children: ReactNode;
}
export const TrackingLoader = t0 => {
  const $ = _c(29);
  const {
    dataPromise,
    buildCheckoutUrl,
    children
  } = t0;
  const data = use(dataPromise);
  const urlParams = useParams();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("item_id");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const itemId = t1;
  const [hydrated, setHydrated] = useState(false);
  let t2;
  if ($[2] !== pathname) {
    const channelSegments = pathname.split("/").filter(Boolean);
    const channelSection = channelSegments[1] ?? "";
    const upsellIdFromPath = channelSegments[2] ?? null;
    const isUpsellPath = channelSection === "up";
    t2 = isUpsellPath && upsellIdFromPath ? readUpsellChannel(upsellIdFromPath) : null;
    $[2] = pathname;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const upsellChannel = t2;
  const channelId = upsellChannel ? upsellChannel : urlParams.channel ? urlParams.channel as string : "store";
  let t3;
  let t4;
  if ($[4] !== channelId) {
    t3 = () => {
      window.channel = channelId;
    };
    t4 = [channelId];
    $[4] = channelId;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useLayoutEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== channelId) {
    t5 = () => {
      registerPostHogProperties({
        channel: channelId
      });
    };
    t6 = [channelId];
    $[7] = channelId;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  if ($[10] !== buildCheckoutUrl || $[11] !== itemId || $[12] !== pathname || $[13] !== urlParams) {
    t7 = () => {
      if (buildCheckoutUrl && pathname.includes("/lp/")) {
        const market = urlParams.market as string | undefined;
        const channel = urlParams.channel as string | undefined;
        const origin = window.location.origin;
        if (market && channel) {
          let checkoutHref = `${origin}/${market}/lp/${channel}/checkout`;
          if (itemId) {
            checkoutHref = `${checkoutHref}?item_id=${encodeURIComponent(itemId)}`;
          }
          window.checkout_url = checkoutHref;
        } else {
          delete window.checkout_url;
        }
      } else {
        delete window.checkout_url;
      }
    };
    $[10] = buildCheckoutUrl;
    $[11] = itemId;
    $[12] = pathname;
    $[13] = urlParams;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const syncCheckoutUrl = t7;
  let t8;
  if ($[15] !== data || $[16] !== syncCheckoutUrl) {
    t8 = () => {
      if (!data) {
        delete window.$r;
        delete window.$vb;
        syncCheckoutUrl();
        bootPosthog();
        window.dispatchEvent(new Event("beacon:ready"));
        setHydrated(true);
        return;
      }
      window.$r = {
        bot: data.bot,
        ip: data.ip,
        isp: data.isp,
        loc: data.loc ?? undefined,
        geo: data.geo ?? undefined,
        proxy: data.proxy,
        ua: data.ua ?? undefined,
        w: data.weather,
        vibes: data.vibes ?? undefined
      };
      if (data.vibes) {
        window.$vb = data.vibes;
      } else {
        delete window.$vb;
      }
      const url = new URL(window.location.href);
      url.search = data.url.search;
      for (const param of DATA_ONLY_PARAMS) {
        url.searchParams.delete(param);
      }
      const currentHistoryState = window.history.state ?? {};
      window.history.replaceState(currentHistoryState, "", url.pathname + url.search + url.hash);
      bootPosthog();
      registerPostHogProperties({
        vibes: data.vibes
      });
      syncCheckoutUrl();
      window.dispatchEvent(new Event("beacon:ready"));
      setHydrated(true);
    };
    $[15] = data;
    $[16] = syncCheckoutUrl;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== channelId || $[19] !== data || $[20] !== syncCheckoutUrl) {
    t9 = [data, syncCheckoutUrl, channelId];
    $[18] = channelId;
    $[19] = data;
    $[20] = syncCheckoutUrl;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  useEffect(t8, t9);
  if (!hydrated) {
    return null;
  }
  if (STAGE === "dev") {
    let t10;
    if ($[22] !== children) {
      t10 = <>{children}</>;
      $[22] = children;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    return t10;
  }
  let t10;
  if ($[24] !== data) {
    t10 = data && <StoreTrackingScripts data={data} />;
    $[24] = data;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== children || $[27] !== t10) {
    t11 = <>{t10}{children}</>;
    $[26] = children;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
};